import React, { useEffect, useState, useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import Layout from 'components/sales/layout/layout';
import CardSection from 'components/sales/cardSection/cardSection';
import localization from 'assets/localization/localization';
import { BaseUrl } from 'contexts/baseUrl/baseUrl';
import { fetchDynamicContentView } from 'services/api/dynamicContentsApi';
import {
  normalizeImageItemsFromContent,
  parseDisplayDate,
  canEditDynamicContent,
} from '../dynamicContentUtils';
import DeactivateDynamicContentPopup from '../DeactivateDynamicContentPopup';
import TargetIdsViewBlock from '../TargetIdsViewBlock';
import '../dynamicContentsView.css';

const formatRuleType = (rule) => {
  if (!rule) return '—';
  const map = { all: 'All', include: 'Include', exclude: 'Exclude' };
  return map[rule] || rule;
};

const formatStatus = (status) =>
  status === 1 || status === '1' ? 'Active' : 'Inactive';

const DetailRow = ({ label, value }) => (
  <div className="col-md-6 mb-3">
    <div className="client-dashboard-banner-detail-label">{label}</div>
    <div className="client-dashboard-banner-detail-value">{value ?? '—'}</div>
  </div>
);

const DynamicContentsView = () => {
  const { id } = useParams();
  const history = useHistory();
  const { baseUrl } = useContext(BaseUrl);
  const [isLoading, setIsLoading] = useState(true);
  const [content, setContent] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [showDeactivatePopup, setShowDeactivatePopup] = useState(false);

  const loadDetails = async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      const data = await fetchDynamicContentView(id);
      setContent(data);
    } catch (err) {
      setContent(null);
      setLoadError(err?.message || 'Failed to load details');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) loadDetails();
  }, [id]);

  const contentPayload = content?.content || {};
  const banners = normalizeImageItemsFromContent(contentPayload).filter(
    (item) => item.image_url
  );
  const targetIds = (content?.targets || [])
    .map((t) => t.target_id)
    .filter((tid) => tid != null && tid !== '');
  const isActive = content?.status === 1 || content?.status === '1';
  const canEdit = canEditDynamicContent(content);
  const showEditActions = isActive && canEdit;

  const headerBtns = () =>
    showEditActions ? (
      <div className="dynamic-contents-header-actions">
        <Button
          variant="primary"
          size="sm"
          onClick={() => history.push(`${baseUrl}/dynamic-contents/edit/${id}`)}
        >
          {localization.editDynamicContent}
        </Button>
        <Button
          variant="outline-danger"
          size="sm"
          onClick={() => setShowDeactivatePopup(true)}
        >
          {localization.deactivateDynamicContent}
        </Button>
      </div>
    ) : null;

  return (
    <Layout
      className="client-dashboard-banner-view-page"
      title={localization.clientDashboardContentDetails}
      Buttons={headerBtns}
      isGoback
      isLoading={isLoading}
      linkFlag
      linkUrl={`${baseUrl}/dynamic-contents`}
      useNewHeaderLayout
    >
      {loadError && (
        <div className="alert alert-danger" role="alert">
          {loadError}
        </div>
      )}

      {content && !loadError && (
        <>
          <CardSection title={localization.clientDashboardContentDetails} panelN="">
            <div className="row">
              <DetailRow label={localization.title} value={content.title} />
              <DetailRow label={localization.ruleType} value={formatRuleType(content.rule_type)} />
              <DetailRow label={localization.status} value={formatStatus(content.status)} />
              <DetailRow
                label={localization.createdBy}
                value={content.created_by_name}
              />
              <DetailRow
                label={localization.startDate}
                value={parseDisplayDate(content.start_at)}
              />
              <DetailRow
                label={localization.endDate}
                value={parseDisplayDate(content.end_at)}
              />
              <TargetIdsViewBlock
                targetIds={targetIds}
                ruleType={content.rule_type}
              />
            </div>
          </CardSection>

          <CardSection title={localization.clientDashboardContents} panelN="">
            {banners.length === 0 ? (
              <p className="text-muted mb-0">{localization.noClientDashboardContentImages}</p>
            ) : (
              <div className="client-dashboard-banner-gallery">
                {banners.map((banner, index) => (
                  <div
                    key={`banner-${index}-${banner.image_url}`}
                    className="client-dashboard-banner-card"
                  >
                    <div className="client-dashboard-banner-card-title">
                      {localization.image} {index + 1}
                    </div>
                    <a
                      href={banner.image_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="client-dashboard-banner-image-link"
                    >
                      <img
                        src={banner.image_url}
                        alt={`${localization.image} ${index + 1}`}
                        className="client-dashboard-banner-image"
                      />
                    </a>
                    {banner.redirect_url ? (
                      <div className="client-dashboard-banner-redirect small mt-2">
                        <span className="text-muted">{localization.redirectUrl}: </span>
                        <a
                          href={banner.redirect_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {banner.redirect_url}
                        </a>
                      </div>
                    ) : (
                      <div className="text-muted small mt-2">No redirection URL</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardSection>
        </>
      )}

      <DeactivateDynamicContentPopup
        show={showDeactivatePopup}
        onClose={() => setShowDeactivatePopup(false)}
        content={content}
        onSuccess={() => {
          setShowDeactivatePopup(false);
          history.push(`${baseUrl}/dynamic-contents`);
        }}
      />
    </Layout>
  );
};

export default DynamicContentsView;
